// features/ups/ManageAgencies.js (page shell only — keep your existing fetching + socket)
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAgencies, deleteAgency, selectAllAgencies
} from '../agencies/agenciesSlice';
import { MoreHorizontal, Plus, Search, ChevronRight } from 'lucide-react';

import CreateAgencyWizardModal from './modals/CreateAgencyWizardModal';
import AddAccountModal from './modals/AddAccountModal';
import ConfirmDeleteModal from './modals/ConfirmDeleteModal';
import AgencyDrawer from './modals/AgencyDrawer';

// Inline, file-local menu component
function RowActionsMenu({ onAdd, onDelete }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const firstItemRef = React.useRef(null);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeydown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeydown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeydown);
    };
  }, []);

  React.useEffect(() => {
    if (open) firstItemRef.current?.focus();
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center justify-center h-9 w-9 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <MoreHorizontal size={18} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-44 z-50 rounded-md border bg-white shadow-lg overflow-hidden"
        >
          <button
            ref={firstItemRef}
            role="menuitem"
            onClick={() => { setOpen(false); onAdd?.(); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:outline-none"
          >
            Add account
          </button>
          <button
            role="menuitem"
            onClick={() => { setOpen(false); onDelete?.(); }}
            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 focus:outline-none"
          >
            Delete agency
          </button>
        </div>
      )}
    </div>
  );
}

export default function ManageAgencies() {
  const dispatch = useDispatch();
  const agencies = useSelector(selectAllAgencies);
  const loading  = useSelector(s => s.agencies.loading);
  const error    = useSelector(s => s.agencies.error);

  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [currentAgency, setCurrentAgency] = useState(null);

  useEffect(() => { dispatch(fetchAgencies()); }, [dispatch]);

  const filtered = agencies.filter(a =>
    a.name?.toLowerCase().includes(query.toLowerCase())
  );

  const openDrawer = (ag) => { setCurrentAgency(ag); setDrawerOpen(true); };
  const openAdd = (ag) => { setCurrentAgency(ag); setAddOpen(true); };
  const openDelete = (ag) => { setCurrentAgency(ag); setDeleteOpen(true); };

  return (
    <>
      {/* Main page content with spacing */}
      <div className="space-y-6">
        {/* Header / Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-2xl font-bold">Manage Agencies</h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5" size={16} />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search agencies…"
                className="pl-8 pr-3 py-2 border rounded-md"
              />
            </div>
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Plus size={16} /> New agency
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow">
          {loading ? (
            <div className="p-6 text-gray-500">Loading…</div>
          ) : error ? (
            <div className="p-6 text-red-600">{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">ID</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Name</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map(ag => (
                    <tr key={ag.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3">{ag.id}</td>
                      <td className="px-6 py-3">
                        <button
                          onClick={() => openDrawer(ag)}
                          className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                        >
                          {ag.name} <ChevronRight size={16} />
                        </button>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex justify-end">
                          <RowActionsMenu
                            onAdd={() => openAdd(ag)}
                            onDelete={() => openDelete(ag)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-6 py-8 text-center text-gray-500">
                        No agencies found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Overlays OUTSIDE the spaced stack (prevents the top gap) */}
      {createOpen && (
        <CreateAgencyWizardModal
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {addOpen && currentAgency && (
        <AddAccountModal
          isOpen={addOpen}
          agency={currentAgency}
          onClose={() => { setAddOpen(false); setCurrentAgency(null); }}
        />
      )}

      {deleteOpen && currentAgency && (
        <ConfirmDeleteModal
          isOpen={deleteOpen}
          agency={currentAgency}
          onConfirm={async () => {
            await dispatch(deleteAgency(currentAgency.id));
            setDeleteOpen(false);
            setCurrentAgency(null);
          }}
          onClose={() => { setDeleteOpen(false); setCurrentAgency(null); }}
        />
      )}

      {drawerOpen && currentAgency && (
        <AgencyDrawer
          isOpen={drawerOpen}
          agency={currentAgency}
          onClose={() => {
            // keep currentAgency so AddAccountModal knows which agency to use
            setDrawerOpen(false);
          }}
          onAddAccount={() => {
            // optional: close the drawer before opening the Add Account modal
            setDrawerOpen(false);
            setAddOpen(true);
          }}
        />
      )}
    </>
  );
}
